import "./desktop-layout.scss";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { 
  shoonitnewlogo, 
  dot, 
  plusIcon, 
  userSquare, 
  uploadIcon, 
  calculatorIcon, 
  lessonBuilderIcon,
  lockIcon,
  Loader 
} from "../../assets/images/index";

import { Link } from 'react-router-dom';
import DndFileUpload from "../../components/file-upload";
import CreateNewExerciseModal from "../../components/create-new-exercise";
import { logout } from "../../utils/authUtils";
import NewExercisesPage from "../../pages/admin/new-exercises";
import ExerciseList from "../../pages/admin/exercise-list";
import StudentList from "../../pages/admin/student-list";
import SearchExercise from "../../pages/admin/search-exercise";
import LessonsBuilder from "../../pages/admin/lessons-builder";
import SolutionAnalysis from "../../pages/admin/solution-analysis";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";
import { UserService } from "../../services/user/user.service";
import { useLessonStore } from "../../utils/lessonStore";
import { RouterConstant } from "../../routers/index";

// Feature toggle: Keep code intact but hide from menu and disable real-time API call
const ENABLE_STUDENT_LIST = false;

const MainDesktopLayout = () => {
  const { t } = useTranslation();
  const { ROUTERS_PATH } = RouterConstant;
  const setUnreadCount = useLessonStore((state) => state.setUnreadCount);
  const newExercisesRef = useRef(null);
  const exerciseListRef = useRef(null);
  const searchExerciseService = new SearchExerciseService();
  const userService = new UserService();
  const fetchTopics = useLessonStore((state) => state.fetchTopics);

  const [selectedView, setSelectedView] = useState('newExercises');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newExerciseCount, setNewExerciseCount] = useState(0);
  const [exerciseListCount, setExerciseListCount] = useState(0);
  const [studentsCount, setStudentsCount] = useState(0);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [loadingStudentCounts, setLoadingStudentCounts] = useState(true);
  const [createExerciseOpen, setCreateExerciseOpen] = useState(false);

  const viewSectionTitles = {
    newExercises: t('exercisesSection'),
    exerciseList: t('exercisesSection'),
    searchExercises: t('exercisesSection'),
    solutionAnalysis: t('exercisesSection'),
    studentList: t('exercisesSection'),
    lessonsBuilder: t('teachersSection'),
    teacherDashboard: t('teachersSection'),
  };

  const viewTitles = {
    newExercises: t('newExercisesNotInDB'),
    exerciseList: t('existingExercisesInDB'),
    studentList: t('studentsSection'),
    searchExercises: t('searchByImage'),
    solutionAnalysis: t('solutionAnalysis'),
    lessonsBuilder: t('createLessonContent'),
    teacherDashboard: t('teacherDashboard'),
  };

  const renderContent = () => {
    switch (selectedView) {
      case 'newExercises':
        return <NewExercisesPage ref={newExercisesRef} fetchCounts={fetchCounts} />;
      case 'exerciseList':
        return <ExerciseList ref={exerciseListRef} fetchCounts={fetchCounts} />;
      case 'studentList':
        return ENABLE_STUDENT_LIST ? <StudentList ref={exerciseListRef} fetchStudentCounts={fetchStudentCounts} /> : null;
      case 'searchExercises':
        return <SearchExercise />;
      case 'solutionAnalysis':
        return <SolutionAnalysis />;
      case 'lessonsBuilder':
        return <LessonsBuilder />;
      default:
        return null;
    }
  };

  const fetchCounts = async () => {
    setLoadingCounts(true);
    try {
      const { exercise_list_count, new_exercise_count } = await searchExerciseService.getExerciseCounter();
      setExerciseListCount(exercise_list_count);
      setNewExerciseCount(new_exercise_count);
    } catch (error) {
      console.error("Failed to fetch exercise counters:", error);
    } finally {
      setLoadingCounts(false);
    }
  };

  const fetchStudentCounts = async () => {
    setLoadingStudentCounts(true);
    try {
      const response = await userService.fetchStudetCount();
      setStudentsCount(response.student_count);
    } catch (error) {
      console.error("Failed to fetch exercise counters:", error);
    } finally {
      setLoadingStudentCounts(false);
    }
  };

  useEffect(() => {
    fetchTopics(searchExerciseService);
    if (ENABLE_STUDENT_LIST) {
      fetchStudentCounts();
    }
    fetchCounts();
  }, []);

  const fetchExercises = async () => {
    if (newExercisesRef.current?.fetchExercises) {
      newExercisesRef.current.fetchExercises();
    }
  };

  const fetchExercisesFromList = () => {
    if (exerciseListRef.current?.fetchExercises) {
      exerciseListRef.current.fetchExercises();
    }
  };

  const onCloseUpload = () => {
    setUploadOpen(false);
    fetchCounts();
    fetchExercises();
  };

  return (
    <>
      <div className="dashboard-layout" dir="rtl">        
        <aside>
          <div className="logo_container">
            <img src={shoonitnewlogo} alt="" />
          </div>
          <nav className="dashboard-nav">
            {/* Section 1: מנהל מערכת */}
            <div className="nav-section">
              <div className="nav-section-title">{t('exercisesSection')}</div>
              <ul>
                <li className={`nav-item ${selectedView === 'newExercises' ? 'active' : ''}`} onClick={() => setSelectedView('newExercises')} >
                  <Link to="/dashboard">
                    <img src={uploadIcon} alt="" />
                    <span className="nav-item-text">{t('newExercisesNotInDB')}</span>
                    <span className="nav-item-count">
                      {loadingCounts ? (
                        <img src={Loader} alt="" className="count-spinner" />
                      ) : (
                        newExerciseCount || 0
                      )}
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${selectedView === 'exerciseList' ? 'active' : ''}`} onClick={() => setSelectedView('exerciseList')}>
                  <Link to="/dashboard">
                    <img src={calculatorIcon} alt="" />
                    <span className="nav-item-text">{t('existingExercisesInDB')}</span>
                    <span className="nav-item-count">
                      {loadingCounts ? (
                        <img src={Loader} alt="" className="count-spinner" />
                      ) : (
                        exerciseListCount || 0
                      )}
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${selectedView === 'searchExercises' ? 'active' : ''}`} onClick={() => setSelectedView('searchExercises')} >
                  <Link to="/dashboard">
                    <img src={uploadIcon} alt="" />
                    <span className="nav-item-text">{t('searchByImage')}</span>
                  </Link>
                </li>
                <li className={`nav-item ${selectedView === 'solutionAnalysis' ? 'active' : ''}`} onClick={() => setSelectedView('solutionAnalysis')} >
                  <Link to="/dashboard">
                    <img src={calculatorIcon} alt="" />
                    <span className="nav-item-text">{t('solutionAnalysis')}</span>
                  </Link>
                </li>
                {ENABLE_STUDENT_LIST && (
                  <li className={`nav-item ${selectedView === 'studentList' ? 'active' : ''}`} onClick={() => setSelectedView('studentList')}>
                    <Link to="/dashboard">
                      <img src={userSquare} alt="" />
                      <span className="nav-item-text">{t('studentsSection')}</span>
                      <span className="nav-item-count">
                        {loadingStudentCounts ? (
                          <img src={Loader} alt="" className="count-spinner" />
                        ) : (
                          studentsCount || 0
                        )}
                      </span>
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Section 2: מורים */}
            <div className="nav-section">
              <div className="nav-section-title">{t('teachersSection')}</div>
              <ul>
                <li className={`nav-item ${selectedView === 'lessonsBuilder' ? 'active' : ''}`} onClick={() => setSelectedView('lessonsBuilder')} >
                  <Link to="/dashboard">
                    <img src={lessonBuilderIcon} alt="" />
                    <span className="nav-item-text">{t('createLessonContent')}</span>
                  </Link>
                </li>
                <li className={`nav-item ${selectedView === 'teacherDashboard' ? 'active' : ''}`} onClick={() => setSelectedView('teacherDashboard')}>
                  <Link to={ROUTERS_PATH.TEACHER_DASHBOARD}>
                    <img src={userSquare} alt="" />
                    <span className="nav-item-text">{t('teacherDashboard')}</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Bottom Action: יציאה */}
            <ul>
              <li onClick={() => logout()} className="nav-item logout">        
                <span className="nav-item-text">{t('logout')}</span>             
              </li>   
            </ul>
          </nav>
        </aside>
        <main>
          <header>
            <div>
              <h1>{viewTitles[selectedView]}</h1>
              <p>
                <span>{viewSectionTitles[selectedView]}</span>
                <img src={dot} alt="" />
                <span>{viewTitles[selectedView]}</span>
              </p>
            </div>
            {selectedView === 'newExercises' && (
              <div className="header-actions">
                <button className="add-exercise-button" onClick={() => setUploadOpen(true)}>
                  <img src={plusIcon} alt="" />
                  <span>{t('newExercise')}</span>
                </button>
                <DndFileUpload
                  open={uploadOpen}
                  onClose={() => onCloseUpload(false)}
                  onCancel={() => setUploadOpen(false)}
                />
              </div>
            )}
            {selectedView === 'exerciseList' && (
              <div className="header-actions">
                <button className="add-exercise-button" onClick={() => setCreateExerciseOpen(true)}>
                  <img src={plusIcon} alt="" />
                  <span>{t('createNewExercise')}</span>
                </button>
                <CreateNewExerciseModal 
                  open={createExerciseOpen} 
                  onClose={() => setCreateExerciseOpen(false)} 
                  fetchCounts={fetchCounts}
                  fetchExercisesFromList={fetchExercisesFromList}
                />
              </div>
            )}
          </header>
          <section>
            <div className='dashboard-container'>
              {renderContent()}
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default MainDesktopLayout;
